import { Logger, Utils, StringUtils, ArrayUtils, engineComponent } from "neo-common";
import { ComponentTypes } from "../../ComponentTypes";
import { ITweenable, Tweens, TweensAttributes } from "../../Effects/Tweens";
import { SwitchStyle } from "../Styles/SwitchStyle";
import { StyleManager } from "../../Managers/StyleManager";
import { IElement } from "../Interfaces/IElement";
import { Attributes } from "../../../Utils/Attributes";

/**
 * ShapeElement allows you to display vector art in the display list.
 * Example:
 *   <ShapeElement Class="ShapeElement" X="0" Y="0" Width="100" Height="100" Color="FF0000" Mask="ShapeElement" />
 *   <ShapeElement Class="ShapeElement" Draw="Circle" X="0" Y="0" Radius="500" Color="FF0000"/>
 *   <ShapeElement Class="ShapeElement" Draw="Ellipse" X="0" Y="0" Width="300" Height="500" Color="FF0000"/>
 *   <ShapeElement Class="ShapeElement" Draw="Polygon" X="0" Y="0" Color="FF0000" Path="1373;76|1374;85|1375;97|1377;114|1378;130|1379;145|1286;71|1316;72"/>
 *   <ShapeElement Class="ShapeElement" Draw="Rect" X="110" Y="0" Width="500" Height="500" Color="FF0000"/>
 *   <ShapeElement Class="ShapeElement" Draw="RoundedRect" X="0" Y="0" Width="500" Height="500" Radius="50" Color="FF0000"/>
 *
 * @class ShapeElement
 * @extends PIXI.Graphics
 * @implements IElement
 *
 * @date 7.1.2019
 */
@engineComponent(ComponentTypes.DisplayObject)
export class ShapeElement extends PIXI.Graphics implements IElement {
    public _uid: string;
    public _color: number = 0xff0000;
    public _width: number;
    public _height: number;
    public _lineWidth: number;
    public _lineColor: number;
    public _fillAlpha: number;
    public _radius: number;
    public _draw: string = "Rect";
    protected style: SwitchStyle;

    constructor() {
        super();

        this._uid = `${this.componentName}_${Utils.generateUuid()}`;
    }

    protected internalDestroy(options?: PIXI.DestroyOptions | boolean): void {
        Tweens.removeTweens(this);
    }

    public create(node: Element): void {
        this.createByNode(node);
        this.updateByNode(node);
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    createByNode(node: Element): void {
        if (DEBUG) {
            if (
                node.hasAttribute(ShapeElementAttributes.DEBUG) &&
                node.getAttribute(ShapeElementAttributes.DEBUG) === "true"
            ) {
                debugger;
            }
        }

        if (this.name == null) {
            this.name = node.nodeName;
        }

        if (node.hasAttribute(ShapeElementAttributes.STYLE)) {
            this.style = <SwitchStyle>StyleManager.Styles[node.getAttribute(ShapeElementAttributes.STYLE)];
            if (this.style != null) {
            } else {
                if (DEBUG) {
                    this.log("ERROR no style with name: " + node.getAttribute(ShapeElementAttributes.STYLE));
                }
            }
        }

        this._color = Attributes.assignIntAttribute(node, ShapeElementAttributes.COLOR, this._color, 16);
        this._fillAlpha = Attributes.assignFloatAttribute(node, ShapeElementAttributes.FILL_ALPHA, this._fillAlpha);
        this._lineWidth = Attributes.assignIntAttribute(node, ShapeElementAttributes.LINE_WIDTH, this._lineWidth);
        this._lineColor = Attributes.assignIntAttribute(node, ShapeElementAttributes.LINE_COLOR, this._lineColor, 16);

        if (node.hasAttribute(ShapeElementAttributes.SCALE_X)) {
            this.scale.x = parseFloat(node.getAttribute(ShapeElementAttributes.SCALE_X));
        }

        if (node.hasAttribute(ShapeElementAttributes.SCALE_Y)) {
            this.scale.y = parseFloat(node.getAttribute(ShapeElementAttributes.SCALE_Y));
        }

        if (node.hasAttribute(ShapeElementAttributes.SCALE)) {
            this.scale.x = parseFloat(node.getAttribute(ShapeElementAttributes.SCALE));
            this.scale.y = parseFloat(node.getAttribute(ShapeElementAttributes.SCALE));
        }

        if (node.hasAttribute(ShapeElementAttributes.X)) {
            this.position.x = parseInt(node.getAttribute(ShapeElementAttributes.X));
        }

        if (node.hasAttribute(ShapeElementAttributes.Y)) {
            this.position.y = parseInt(node.getAttribute(ShapeElementAttributes.Y));
        }

        if (node.hasAttribute(ShapeElementAttributes.WIDTH)) {
            this._width = parseInt(node.getAttribute(ShapeElementAttributes.WIDTH));
        }

        if (node.hasAttribute(ShapeElementAttributes.HEIGHT)) {
            this._height = parseInt(node.getAttribute(ShapeElementAttributes.HEIGHT));
        }

        if (node.hasAttribute(ShapeElementAttributes.RADIUS)) {
            this._radius = parseInt(node.getAttribute(ShapeElementAttributes.RADIUS));
        }

        if (node.hasAttribute(ShapeElementAttributes.DRAW)) {
            this.clear();
            this.beginFill(this._color, this._fillAlpha);
            this.lineStyle(this._lineWidth, this._lineColor);
            this.draw(node, node.getAttribute(ShapeElementAttributes.DRAW));
            this.endFill();
        } else {
            // This block is for backwards compatibility
            if (node.hasAttribute(ShapeElementAttributes.WIDTH)) {
                this._width = parseInt(node.getAttribute(ShapeElementAttributes.WIDTH));
            } else {
                if (DEBUG) {
                    this.log("ERROR " + ShapeElementAttributes.WIDTH + " attribute doesn't exits");
                }
            }

            if (node.hasAttribute(ShapeElementAttributes.HEIGHT)) {
                this._height = parseInt(node.getAttribute(ShapeElementAttributes.HEIGHT));
            } else {
                if (DEBUG) {
                    this.log("ERROR " + ShapeElementAttributes.HEIGHT + " attribute doesn't exits");
                }
            }

            if (this._width != null && this._height != null) {
                this.clear();
                this.beginFill(this._color, this._fillAlpha);
                this.lineStyle(this._lineWidth, this._lineColor);
                this.drawRect(0, 0, this._width, this._height);
                this.endFill();
            }
        }

        if (node.hasAttribute(ShapeElementAttributes.VISIBLE)) {
            this.visible = StringUtils.parseBoolean(node.getAttribute(ShapeElementAttributes.VISIBLE));
        }

        /*if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.createElement(this, this.getDebugInfo());
            }
        }*/
    }

    /**
     * Draw object by type.
     * @param node
     * @param {string} objectType
     */
    private draw(node: Element, objectType: string) {
        this._draw = objectType;
        switch (objectType) {
            case "Circle":
                this.drawCircle(0, 0, this._radius);
                break;
            case "Ellipse":
                this.drawEllipse(0, 0, this._width, this._height);
                break;
            case "Polygon":
                const path = node.hasAttribute("Path") ? ArrayUtils.stringArray(node.getAttribute("Path"), "|") : [];
                let points: PIXI.Point[] = [];
                for (let i = 0; i < path.length; i++) {
                    const point = ArrayUtils.numberArray(path[i]);
                    points.push(new PIXI.Point(point[0], point[1]));
                }
                this.drawPolygon(points);
                break;
            case "Rect":
                this.drawRect(0, 0, this._width, this._height);
                break;
            case "RoundedRect":
                const multiple = node.hasAttribute("Path")
                    ? ArrayUtils.arraysNumbers(node.getAttribute("Path"), "|")
                    : [];
                if (multiple.length == 0) {
                    this.drawRoundedRect(0, 0, this._width, this._height, this._radius);
                } else {
                    for (let i = 0; i < multiple.length; i++) {
                        this.drawRoundedRect(multiple[i][0], multiple[i][1], this._width, this._height, this._radius);
                    }
                }
                break;
            default:
                if (DEBUG) {
                    this.log("ERROR objectType: " + objectType + " not supported.");
                }
                break;
        }
    }

    /**
     * @param node
     */
    public updateByNode(node: Element): void {
        if (DEBUG) {
            if (
                node.hasAttribute(ShapeElementAttributes.DEBUG) &&
                node.getAttribute(ShapeElementAttributes.DEBUG) === "true"
            ) {
                debugger;
            }
        }

        Tweens.removeTweens(this);

        if (node.hasAttribute(ShapeElementAttributes.VISIBLE)) {
            this.visible = StringUtils.parseBoolean(node.getAttribute(ShapeElementAttributes.VISIBLE));
        }

        if (node.hasAttribute(ShapeElementAttributes.ALPHA)) {
            this.alpha = parseFloat(node.getAttribute(ShapeElementAttributes.ALPHA));
        }

        this.pivot.x = Attributes.assignIntAttribute(node, ShapeElementAttributes.PIVOT_X, this.pivot.x);
        this.pivot.y = Attributes.assignIntAttribute(node, ShapeElementAttributes.PIVOT_Y, this.pivot.y);

        if (node.hasAttribute(ShapeElementAttributes.ROTATION)) {
            this.rotation = this.degrees2radians(parseFloat(node.getAttribute(ShapeElementAttributes.ROTATION)));
        }

        if (node.hasAttribute(ShapeElementAttributes.X)) {
            this.position.x = parseInt(node.getAttribute(ShapeElementAttributes.X));
        }

        if (node.hasAttribute(ShapeElementAttributes.Y)) {
            this.position.y = parseInt(node.getAttribute(ShapeElementAttributes.Y));
        }

        if (node.hasAttribute(ShapeElementAttributes.SCALE_X)) {
            this.scale.x = parseFloat(node.getAttribute(ShapeElementAttributes.SCALE_X));
        }

        if (node.hasAttribute(ShapeElementAttributes.SCALE_Y)) {
            this.scale.y = parseFloat(node.getAttribute(ShapeElementAttributes.SCALE_Y));
        }

        if (node.hasAttribute(ShapeElementAttributes.SCALE)) {
            this.scale.x = parseFloat(node.getAttribute(ShapeElementAttributes.SCALE));
            this.scale.y = parseFloat(node.getAttribute(ShapeElementAttributes.SCALE));
        }

        if (node.hasAttribute(ShapeElementAttributes.MASK)) {
            let next: Element = node.parentElement.firstElementChild;
            while (next != null) {
                if (next.nodeName === node.getAttribute(ShapeElementAttributes.MASK)) {
                    let shapeElement: ShapeElement = new ShapeElement();
                    shapeElement.create(next);
                    this.mask = shapeElement;
                }
                next = next.nextElementSibling;
            }

            if (this.mask == null) {
                if (DEBUG) {
                    this.log("ERROR no element with name: " + node.getAttribute(ShapeElementAttributes.MASK));
                }
            }
        }

        this.runAlphaTween(node);
        this.runPositionTween(node);
        this.runRotationTween(node);
        this.runScaleTween(node);
        this.runTween(node);

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        /*if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.updateDebugInfo(this, this.getDebugInfo());
            }
        }*/
    }

    /**
     * Runs the alpha tween
     * @method runAlphaTween
     * @param node
     * @returns {boolean}
     */
    public runAlphaTween(node: Element): boolean {
        if (!node.hasAttribute(ShapeElementAttributes.ALPHA_TWEEN)) {
            return false;
        }

        let alphaTween: Array<number> = ArrayUtils.numberArray(node.getAttribute(ShapeElementAttributes.ALPHA_TWEEN));

        const alphas: Array<number> = alphaTween || [];

        const tweenEase: string = Attributes.assignStringAttribute(node, ShapeElementAttributes.ALPHA_TWEEN_EASE, null);
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            ShapeElementAttributes.ALPHA_TWEEN_DURATION,
            120,
        );
        const tweenDelay: number = Attributes.assignIntAttribute(node, ShapeElementAttributes.ALPHA_TWEEN_DELAY, 0);
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ShapeElementAttributes.ALPHA_TWEEN_LOOP,
            null,
        );

        Tweens.alphaTween(this, { alphas, tweenEase, tweenDuration, tweenDelay, tweenLoop });
    }

    /**
     * Runs the position tween
     * @method runPositionTween
     * @param node
     * @returns {boolean}
     */
    public runPositionTween(node: Element): boolean {
        if (
            !node.hasAttribute(ShapeElementAttributes.POSITION_TWEEN_X) ||
            !node.hasAttribute(ShapeElementAttributes.POSITION_TWEEN_Y)
        ) {
            return false;
        }

        let positionTweenX: Array<number>;
        let positionTweenY: Array<number>;

        let positionTweenPosition: string = Attributes.assignStringAttribute(
            node,
            ShapeElementAttributes.POSITION_TWEEN_POSITION,
            null,
        );
        if (positionTweenPosition == ShapeElementAttributes.POSITION_TWEEN_POSITION_VALUE.RELATIVE) {
            positionTweenX = ArrayUtils.numberArray(node.getAttribute(ShapeElementAttributes.POSITION_TWEEN_X)).map(
                (value) => value + this.position.x,
            );
            positionTweenY = ArrayUtils.numberArray(node.getAttribute(ShapeElementAttributes.POSITION_TWEEN_Y)).map(
                (value) => value + this.position.y,
            );
        } else {
            positionTweenX = ArrayUtils.numberArray(node.getAttribute(ShapeElementAttributes.POSITION_TWEEN_X));
            positionTweenY = ArrayUtils.numberArray(node.getAttribute(ShapeElementAttributes.POSITION_TWEEN_Y));
        }

        const tweenX: Array<number> = positionTweenX || [];
        const tweenY: Array<number> = positionTweenY || [];
        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            ShapeElementAttributes.POSITION_TWEEN_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            ShapeElementAttributes.POSITION_TWEEN_DURATION,
            120,
        );
        const tweenDelay: number = Attributes.assignIntAttribute(node, ShapeElementAttributes.POSITION_TWEEN_DELAY, 0);
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ShapeElementAttributes.POSITION_TWEEN_LOOP,
            null,
        );

        Tweens.positionTween(this, { tweenX, tweenY, tweenEase, tweenDuration, tweenDelay, tweenLoop });
    }

    /**
     * Runs the rotation tween
     * @method runScaleTween
     * @param node
     * @returns {boolean}
     */
    public runRotationTween(node: Element): boolean {
        if (!node.hasAttribute(ShapeElementAttributes.ROTATION_TWEEN)) {
            return false;
        }

        let rotationTween: Array<number> = ArrayUtils.numberArray(
            node.getAttribute(ShapeElementAttributes.ROTATION_TWEEN),
        );

        const degrees: Array<number> = rotationTween || [];

        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            ShapeElementAttributes.ROTATION_TWEEN_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            ShapeElementAttributes.ROTATION_TWEEN_DURATION,
            120,
        );
        const tweenDelay: number = Attributes.assignIntAttribute(node, ShapeElementAttributes.ROTATION_TWEEN_DELAY, 0);
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ShapeElementAttributes.ROTATION_TWEEN_LOOP,
            null,
        );

        Tweens.rotationTween(this, { degrees, tweenEase, tweenDuration, tweenDelay, tweenLoop });
    }

    /**
     * Runs the scale tween
     *
     * @method runScaleTween
     * @param node
     * @returns {boolean}
     */
    public runScaleTween(node: Element): boolean {
        let scaleTweenX: Array<number>;
        let scaleTweenY: Array<number>;
        let scaleTween: Array<PIXI.Point>;
        let tweenValues: Array<PIXI.Point> = [];

        if (node.hasAttribute(ShapeElementAttributes.SCALE_TWEEN_X)) {
            scaleTweenX = ArrayUtils.numberArray(node.getAttribute(ShapeElementAttributes.SCALE_TWEEN_X));
        }

        if (node.hasAttribute(ShapeElementAttributes.SCALE_TWEEN_Y)) {
            scaleTweenY = ArrayUtils.numberArray(node.getAttribute(ShapeElementAttributes.SCALE_TWEEN_Y));
        }

        if (node.hasAttribute(ShapeElementAttributes.SCALE_TWEEN)) {
            scaleTween = ShapeElement._getPointsFromPointsAttribute(
                node.getAttribute(ShapeElementAttributes.SCALE_TWEEN),
            ) as Array<PIXI.Point>;
        }

        if (scaleTween && scaleTween.length) {
            tweenValues = scaleTween;
        } else if (scaleTweenX && scaleTweenY) {
            if (scaleTweenX.length === scaleTweenY.length) {
                tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                    return {
                        x: scaleXValue,
                        y: scaleTweenY[i],
                    } as PIXI.Point;
                });
            }
        } else if (scaleTweenX) {
            tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                return {
                    x: scaleXValue,
                    y: this.scale.y,
                } as PIXI.Point;
            });
        } else if (scaleTweenY) {
            tweenValues = scaleTweenY.map((scaleYValue: number, i: number) => {
                return {
                    x: this.scale.x,
                    y: scaleYValue,
                } as PIXI.Point;
            });
        } else {
            return false;
        }

        const tweenEase: string = Attributes.assignStringAttribute(node, ShapeElementAttributes.SCALE_TWEEN_EASE, null);
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            ShapeElementAttributes.SCALE_TWEEN_DURATION,
            120,
        );
        const tweenDelay: number = Attributes.assignIntAttribute(node, ShapeElementAttributes.SCALE_TWEEN_DELAY, 0);
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ShapeElementAttributes.SCALE_TWEEN_LOOP,
            null,
        );

        Tweens.scaleTween(this, { tweenValues, tweenEase, tweenDuration, tweenDelay, tweenLoop });
    }

    /**
     *
     * @param {Element} node
     * @returns {boolean}
     */
    public runTween(node: Element): boolean {
        if (node.hasAttribute(TweensAttributes.TWEEN)) {
            let tweens: Array<string> = ArrayUtils.stringArray(node.getAttribute(TweensAttributes.TWEEN));

            for (let i: number = 0; i < tweens.length; i++) {
                //return if there are no values to tween
                if (!node.hasAttribute(TweensAttributes.TWEEN_VALUES + i)) {
                    return false;
                }

                let tween: string = tweens[i];

                const tweenValues: Array<number> = this.formatTweenValues(
                    tween,
                    ArrayUtils.stringArray(node.getAttribute(TweensAttributes.TWEEN_VALUES + i)),
                );
                const tweenDuration: Array<number> = ArrayUtils.numberArray(
                    node.getAttribute(TweensAttributes.TWEEN_DURATION + i) || "120",
                );
                const tweenEase: Array<string> = ArrayUtils.stringArray(
                    node.getAttribute(TweensAttributes.TWEEN_EASE + i) || "linear",
                );
                const tweenDelay: Array<number> = ArrayUtils.numberArray(
                    node.getAttribute(TweensAttributes.TWEEN_DELAY + i) || "0",
                );
                const tweenLoop: boolean = Attributes.assignBooleanAttribute(
                    node,
                    TweensAttributes.TWEEN_LOOP + i,
                    null,
                );

                if (tween == "scale") {
                    tween = "scale.x";
                    tweens[i] = "scale.y";
                    i--;
                }

                Tweens.customTween(this, { tween, tweenValues, tweenEase, tweenDuration, tweenDelay, tweenLoop });
            }
        }

        return false;
    }

    /**
     * Clone method used for cloning instance of this element with his main properties.
     * @method clone
     * @return ImageElement
     */
    clone(): ShapeElement {
        let element: ShapeElement = new ShapeElement();
        element.removeChildren();

        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.rotation = this.rotation;
        element.visible = this.visible;
        element.alpha = this.alpha;
        element.tint = this.tint;
        element.blendMode = this.blendMode;
        element.name = this.name;

        element._color = this._color;
        element._fillAlpha = this._fillAlpha;
        element._draw = this._draw;

        if (this._draw === "Rect") {
            element.clear();
            element.beginFill(this._color, this._fillAlpha);
            element.drawRect(0, 0, this._width, this._height);
            element.endFill();
        }

        return element;
    }

    protected formatTweenValues(tween: string, values: Array<string>): Array<number> {
        let tweenValues: Array<number> = [];

        if (tween === "scale") {
            tween = "scale.x";
        }
        let property: Array<string> = tween.split(".");
        let value: number;

        for (let i: number = 0; i < values.length; i++) {
            let current = values[i];
            switch (current[current.length - 1]) {
                case "c": //if we want current value
                    if (property.length == 1) {
                        value = this[property[0]];
                        if (property[0] === "rotation") {
                            value = this.radians2degrees(value);
                        }
                        tweenValues.push(value);
                    } else if (property.length == 2) {
                        tweenValues.push(this[property[0]][property[1]]);
                    }

                    break;
                case "+":
                    value = parseFloat(current.slice(0, -1));
                    if (tweenValues.length > 0) {
                        value = tweenValues[tweenValues.length - 1] + value;
                    }
                    tweenValues.push(value);
                    break;
                case "-":
                    value = parseFloat(current.slice(0, -1));
                    if (tweenValues.length > 0) {
                        value = tweenValues[tweenValues.length - 1] - value;
                    }
                    tweenValues.push(value);
                    break;
                default:
                    tweenValues.push(parseFloat(current));
            }
        }

        return tweenValues;
    }

    /**
     * Converts string from "points attribute" into array of points. E.g. "(1,6);(5,2);2;(2,4)". Point can be specified as single value for both axex.
     * @method _getPointsFromPointsAttribute
     * @return Array<PIXI.Points>
     */
    protected static _getPointsFromPointsAttribute(pointsString: string): Array<{ x: number; y: number }> {
        if (!pointsString) {
            return [];
        }

        return pointsString
            .replace(/\s/g, "")
            .split(";")
            .map((valuesPair: string) =>
                /\(|\)/.test(valuesPair) ? valuesPair.slice(1, -1).split(",") : [valuesPair, valuesPair],
            )
            .map((valuesPair: Array<string>) => {
                return { x: parseFloat(valuesPair[0]), y: parseFloat(valuesPair[1]) };
            });
    }

    /**
     * @property uid - unique identifier of element.
     * @returns {string}
     */
    public get uid(): string {
        return this._uid;
    }

    /**
     * @protected componentName - component type name.
     * @returns {string}
     */
    public get componentName(): string {
        return (this as any).__componentName__;
    }

    /**
     *
     * @param degrees
     * @returns {number}
     */
    public degrees2radians(degrees: number): number {
        return degrees * (Math.PI / 180);
    }

    /**
     *
     * @param degrees
     * @returns {number}
     */
    public radians2degrees(radians: number): number {
        return radians * (180 / Math.PI);
    }

    /**
     * Trace to browser console.
     * @method log
     * @param s string
     * @return void
     */
    protected log(s: string): void {
        if (DEBUG) {
            let compName = (this as any).__componentName__;
            Logger.debugGroup(compName, s);
        }
    }

    /**
     * Log error into console
     * @param s
     */
    protected logError(s: string): void {
        if (DEBUG) {
            let compName = (this as any).__componentName__;
            Logger.errorGroup(compName, s);
        }
    }
}

export const ShapeElementAttributes = {
    /**
     * Alpha (transparency) for this display object. 0 is fully transparent, 1 is fully opaque.
     * @property Alpha
     * @type Number
     * @default 1
     */
    ALPHA: "Alpha",

    ALPHA_TWEEN: "AlphaTween",
    ALPHA_TWEEN_EASE: "AlphaTweenEase",
    ALPHA_TWEEN_DELAY: "AlphaTweenDelay",
    ALPHA_TWEEN_DURATION: "AlphaTweenDuration",
    ALPHA_TWEEN_LOOP: "AlphaTweenLoop",
    /**
     * Shape's fill color.
     * @property Color
     * @type hexadecimal
     * @default ff0000
     */
    COLOR: "Color",
    DRAW: "Draw",
    DEBUG: "Debug",
    FILL_ALPHA: "FillAlpha",
    /**
     * Shape's height.
     * @property Height
     * @type number
     * @default null
     */
    HEIGHT: "Height",
    LINE_COLOR: "LineColor",
    LINE_WIDTH: "LineWidth",
    /**
     * A ShapeElement instance that defines a vector mask (clipping path) for this display object.
     * The shape's transformation will be applied relative to the display object's parent coordinates (as if it were a child of the parent).
     * @property Mask
     * @type string
     * @default null
     */
    MASK: "Mask",
    /**
     * X pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotX
     * @type number
     * @default null
     */
    PIVOT_X: "PivotX",
    /**
     * Y pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotY
     * @type number
     * @default null
     */
    PIVOT_Y: "PivotY",
    POSITION_TWEEN_X: "PositionTweenX",
    POSITION_TWEEN_Y: "PositionTweenY",
    POSITION_TWEEN_EASE: "PositionTweenEase",
    POSITION_TWEEN_DURATION: "PositionTweenDuration",
    POSITION_TWEEN_DELAY: "PositionTweenDelay",
    POSITION_TWEEN_POSITION: "PositionTweenPosition",
    POSITION_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute",
        RELATIVE: "relative",
    },
    POSITION_TWEEN_LOOP: "PositionTweenLoop",
    /**
     * Rotation in degrees for this display object.
     * @property Rotation
     * @type Number
     * @default 0
     */
    ROTATION: "Rotation",
    ROTATION_TWEEN: "RotationTween",
    ROTATION_TWEEN_EASE: "RotationTweenEase",
    ROTATION_TWEEN_DURATION: "RotationTweenDuration",
    ROTATION_TWEEN_DELAY: "RotationTweenDelay",
    ROTATION_TWEEN_LOOP: "RotationTweenLoop",
    RADIUS: "Radius",
    /**
     * The factor to stretch this display object horizontally and vertically. Use this instead of using ScaleX and ScaleY with same value.
     * @property Scale
     * @type Number
     * @default 1
     */
    SCALE: "Scale",
    SCALE_X: "ScaleX",
    /**
     * The factor to stretch this display object vertically.
     * For example, setting scaleY to 0.5 will stretch the display object to half its nominal height. To vertically flip an object, set the scale to a negative number.
     * @property ScaleY
     * @type Number
     * @default 1
     */
    SCALE_Y: "ScaleY",
    SCALE_TWEEN: "ScaleTween",
    SCALE_TWEEN_DELAY: "ScaleTweenDelay",
    /**
     * @deprecated Use SCALE_TWEEN instead SCALE_TWEEN_DURATION
     */
    SCALE_TWEEN_DURATION: "ScaleTweenDuration",
    SCALE_TWEEN_EASE: "ScaleTweenEase",
    SCALE_TWEEN_LOOP: "ScaleTweenLoop",
    SCALE_TWEEN_X: "ScaleTweenX",
    SCALE_TWEEN_Y: "ScaleTweenY",
    STYLE: "Style",
    /**
     * Indicates whether this display object should be rendered to the canvas and included when running the Stage Stage/getObjectsUnderPoint method.
     * @property Visible
     * @type Boolean
     * @default true
     */
    VISIBLE: "Visible",
    /**
     * Shape's width.
     * @property Width
     * @type number
     * @default null
     */
    WIDTH: "Width",
    /**
     * The X (horizontal) position of the shape, relative to its parent.
     * @property X
     * @type Number
     * @default 0
     */
    X: "X",
    /**
     * The Y (vertical) position of the shape, relative to its parent.
     * @property Y
     * @type Number
     * @default 0
     */
    Y: "Y",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Graphics/ShapeElement.ts
 **/